/**
 * WhatsApp inbox notification sound — MP3 with Web Audio fallback.
 * Mute: localStorage key `wa_sound_muted` === '1'
 */
const MUTE_KEY = "wa_sound_muted";
const SOUND_URL = `${process.env.PUBLIC_URL || ""}/sounds/whatsapp-notification.mp3?v=2`;

let audioEl = null;
let audioUnlocked = false;
let webAudioContext = null;

export function isWhatsAppSoundMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWhatsAppSoundMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    // ignore
  }
}

function getWebAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!webAudioContext) {
    webAudioContext = new Ctx();
  }
  return webAudioContext;
}

function playWebAudioFallback() {
  try {
    const ctx = getWebAudioContext();
    if (!ctx) return;

    const pluck = (freq, delay, dur, vol) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(vol, t + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    };

    const start = () => {
      pluck(988, 0, 0.07, 0.38);
      pluck(1318.5, 0.105, 0.09, 0.34);
    };

    if (ctx.state === "suspended") {
      ctx.resume().then(start).catch(() => {});
    } else {
      start();
    }
  } catch {
    // ignore
  }
}

function getAudioElement() {
  if (!audioEl) {
    audioEl = new Audio(SOUND_URL);
    audioEl.preload = "auto";
  } else if (!audioEl.src.includes("v=2")) {
    audioEl = new Audio(SOUND_URL);
    audioEl.preload = "auto";
  }
  return audioEl;
}

/** Call once after user gesture so autoplay is allowed. */
export function unlockWhatsAppNotificationSound() {
  if (audioUnlocked) return;
  try {
    const el = getAudioElement();
    el.volume = 0.01;
    const playPromise = el.play();
    if (playPromise) {
      playPromise
        .then(() => {
          el.pause();
          el.currentTime = 0;
          el.volume = 1;
          audioUnlocked = true;
        })
        .catch(() => {});
    }
  } catch {
    // ignore
  }
  const ctx = getWebAudioContext();
  if (ctx?.state === "suspended") {
    ctx.resume().catch(() => {});
  }
}

export function playWhatsAppMessageSound() {
  if (isWhatsAppSoundMuted()) return;
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    return;
  }

  try {
    const el = getAudioElement();
    el.volume = 1;
    el.currentTime = 0;
    const playPromise = el.play();
    if (playPromise) {
      playPromise.catch(() => playWebAudioFallback());
    }
  } catch {
    playWebAudioFallback();
  }
}

export function getIncomingMessageKey(message) {
  if (!message) return null;
  if (message.id != null) return `id:${message.id}`;
  return `ts:${message.timestamp}|${message.direction}`;
}

export function isIncomingMessage(message) {
  return String(message?.direction || "").toUpperCase() === "INCOMING";
}

export function getOutgoingMessageMetaId(message) {
  return (
    message?.metaMessageId ??
    message?.messageId ??
    message?.wamid ??
    null
  );
}
